import { serverOnly$ } from 'vite-env-only/macros';
import { ComponentProps, useEffect, useMemo } from 'react';
import { compact } from 'lodash-es';
import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from 'react-router';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  UNSAFE_useScrollRestoration,
  data,
  isRouteErrorResponse,
  useLoaderData,
  useRouteError,
} from 'react-router';
import * as Sentry from '@sentry/react';
import { type EventSourceMap, EventSourceProvider } from 'remix-utils/sse/react';
import { getToast } from 'remix-toast';
import {
  BrushCleaning,
  Carrot,
  Gem,
  Handshake,
  Landmark,
  PackageOpen,
  TriangleAlert,
} from 'lucide-react';
import { HTTPError } from 'ky';
import { IntlProvider } from 'react-intl';
import { ColorSchemeScript, mantineHtmlProps, MantineProvider, Stack, Text } from '@mantine/core';
import { notification, Notifications } from 'feature-notifications';
import { AppShell } from 'admin/ui-app-shell';
import { Poller } from 'admin/feature-task-poll';
import { theme } from 'util-mantine-theme';
import { useRevalidateOnWindowFocus } from 'util-hooks';
import { logger } from 'util-logger';
import { config } from 'util-config';
import { getAuthenticatedUserInfo } from 'admin/feature-authentication';
import { getStytchSession } from 'util-authentication';
import interStyles from '@fontsource-variable/inter/index.css?url';
import mantineCore from '@mantine/core/styles.layer.css?url';
import mantineCharts from '@mantine/charts/styles.layer.css?url';
import mantineDates from '@mantine/dates/styles.layer.css?url';
import mantineNotifications from '@mantine/notifications/styles.layer.css?url';
import invariant from 'tiny-invariant';
import styles from './index.css?url';

export const links: LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'stylesheet', href: interStyles },
  { rel: 'stylesheet', href: mantineCore },
  { rel: 'stylesheet', href: mantineCharts },
  { rel: 'stylesheet', href: mantineDates },
  { rel: 'stylesheet', href: mantineNotifications },
  { rel: 'icon', href: '/favicon.svg' },
  { rel: 'mask-icon', href: '/mask-icon.svg', color: '#FF6B47' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
  { rel: 'manifest', href: '/manifest.json', crossOrigin: 'use-credentials' },
];

export const meta: MetaFunction = () => [
  {
    title: 'Huuva Admin',
  },
  { name: 'theme-color', content: '#FF6B47' },
];

export const loader = serverOnly$(async ({ request }: LoaderFunctionArgs) => {
  invariant(getAuthenticatedUserInfo, 'getAuthenticatedUserInfo is required');
  const { toast, headers } = await getToast(request);

  const { member, organization } = await getAuthenticatedUserInfo(request);
  const sessionToken = getStytchSession(request);

  return data(
    {
      toastData: toast,
      sessionToken,
      member,
      organization,
    },
    { headers },
  );
});

const eventSourceMap: EventSourceMap = new Map();

export default function App() {
  UNSAFE_useScrollRestoration({
    getKey: (location) => location.pathname,
  });
  const { toastData } = useLoaderData<NonNullable<typeof loader>>();

  useEffect(() => {
    if (toastData) {
      const title = toastData.message && toastData.description ? toastData.message : undefined;
      const message =
        toastData.message && toastData.description ? toastData.description : toastData.message;
      switch (toastData.type) {
        case 'error':
          notification.error({ title, message });
          break;
        case 'info':
          notification.info({ title, message });
          break;
        case 'success':
          notification.success({ title, message });
          break;
        case 'warning':
          notification.warning({ title, message });
          break;
        default:
          notification.info({ title, message });
      }
    }
  }, [toastData]);

  useRevalidateOnWindowFocus();

  return (
    <html lang="en" {...mantineHtmlProps}>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta charSet="utf-8" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <Meta />
        <Links />
        <ColorSchemeScript />
      </head>
      <body>
        <EventSourceProvider value={eventSourceMap}>
          <IntlProvider locale="en">
            <AppShellWithSession>
              <Notifications />
              <Outlet />
            </AppShellWithSession>
          </IntlProvider>
          <Poller />
        </EventSourceProvider>
        <Scripts />
        <ScrollRestoration />
      </body>
    </html>
  );
}

const handleErrorLogging = async (error: unknown): Promise<void> => {
  if (error instanceof HTTPError) {
    const extras = {
      response_status: error.response.status,
      response_url: error.response.url,
    };
    try {
      const json = await error.response.json();
      logger.error({ ...extras, response_json: json }, "Error loading route's data");
    } catch {
      try {
        const text = await error.response.text();
        logger.error({ ...extras, response_text: text }, "Error loading route's data");
      } catch {
        logger.error(extras, "Error loading route's data");
      }
    }
  } else {
    logger.error(error, 'Error loading route');
  }
};

export function ErrorBoundary() {
  const error = useRouteError();
  let errorHeading;
  let errorMsg;
  if (isRouteErrorResponse(error)) {
    errorHeading = 'An error occurred!';
    errorMsg = `${error.statusText}: ${error.data}`;
  } else {
    errorHeading = 'An unknown error occurred!';
    errorMsg = error instanceof Error ? error.message : 'Unknown error';
    Sentry.captureException(error);
  }
  handleErrorLogging(error);

  return (
    <html lang="en" {...mantineHtmlProps}>
      <head>
        <title>Page not found! | Huuva Admin</title>
        <Meta />
        <Links />
      </head>
      <body>
        <MantineProvider theme={theme}>
          <AppShell>
            <Stack align="center" justify="center">
              <Text component="h2" size="lg">
                {errorHeading}
              </Text>
              <Text component="p" size="md">
                {errorMsg}
              </Text>
            </Stack>
          </AppShell>
        </MantineProvider>
        <Scripts />
      </body>
    </html>
  );
}

function AppShellWithSession({ children }: { children: React.ReactNode }) {
  const { member } = useLoaderData<NonNullable<typeof loader>>();

  const navItems = useMemo(() => {
    if (!member) return undefined;
    const navItems = getNavItems();
    return navItems;
  }, [member]);

  const secondaryNavItems = useMemo(() => {
    if (!member) return undefined;
    const secondaryNavItems = getSecondaryNavItems(member.is_admin);
    return secondaryNavItems;
  }, [member]);

  return (
    <MantineProvider theme={theme}>
      <AppShell navItems={navItems} secondaryNavItems={secondaryNavItems} member={member}>
        {children}
      </AppShell>
    </MantineProvider>
  );
}

function getNavItems(): NonNullable<ComponentProps<typeof AppShell>['navItems']> {
  return compact([
    {
      id: 'markets',
      label: 'Kitchens',
      to: '/markets',
    },
    {
      id: 'partners',
      label: 'Partners',
      subItems: [
        {
          id: 'companies',
          label: 'Companies',
          to: '/companies',
          Icon: Landmark,
        },
        {
          id: 'brands',
          label: 'Brands',
          to: '/brands',
          Icon: Gem,
        },
        {
          id: 'contracts',
          label: 'Contracts',
          to: '/contracts',
          Icon: Handshake,
        },
      ],
    },
    {
      id: 'recipes',
      label: 'Recipes',
      subItems: [
        {
          id: 'all-recipes',
          label: 'All recipes',
          to: '/recipes',
          Icon: Carrot,
        },
        {
          id: 'packaging',
          label: 'Packaging',
          to: '/packaging',
          Icon: PackageOpen,
        },
        {
          id: 'allergens',
          label: 'Allergens',
          to: '/allergens',
          Icon: TriangleAlert,
        },
      ],
    },
    {
      id: 'items',
      label: 'Items',
      subItems: [
        {
          id: 'items-list',
          label: 'Menu Items',
          to: '/items',
          Icon: Gem,
        },
        {
          id: 'lunchitems',
          label: 'Lunch Items',
          to: '/lunchitems',
          Icon: Carrot,
        },
        {
          id: 'menu-cleanup',
          label: 'Cleanup',
          to: '/menu-cleanup',
          Icon: BrushCleaning,
        },
      ],
    },
    config.CAMPAIGNS_URL && {
      id: 'campaigns',
      label: 'Campaigns',
      href: config.CAMPAIGNS_URL,
      target: '_blank',
    },
    {
      id: 'vouchers',
      label: 'Vouchers',
      to: '/vouchers',
    },
    {
      id: 'media',
      label: 'Media',
      to: '/media',
    },
    {
      id: 'publishing',
      label: 'Publishing',
      href: config.PUBLISHING_URL,
      target: '_blank',
    },
  ]);
}
function getSecondaryNavItems(
  isAdmin: boolean,
): NonNullable<ComponentProps<typeof AppShell>['secondaryNavItems']> {
  return [
    {
      id: 'more',
      label: 'More',
      subItems: compact([
        isAdmin && { id: 'members', to: '/members', label: 'Manage members' },
        {
          id: 'data-dashboards',
          label: 'Data dashboards',
          href: 'https://data.huuva.io',
          target: '_blank',
        },
        {
          id: 'kitchen-os',
          label: 'Kitchen OS',
          href:
            config.APP_ENV === 'production'
              ? 'https://admin.huuva.io/kitchen'
              : 'https://admin-staging.huuva.io/kitchen',
          target: '_blank',
        },
      ]),
    },
  ];
}
